import { useState } from "react";
import api from "../../../utils/axiosInstance";
import FormInput from "../../../components/Form/FormInput";
import FormTextarea from "../../../components/Form/TextArea";

const Field = ({ label, value }) => {
  const isEmpty = value === null || value === undefined || value === "";
  return (
    <div className="flex items-start text-sm">
      <span className="w-40 shrink-0 text-xs font-medium text-gray-500 pt-px">
        {label}:
      </span>
      <span className="text-gray-900">{isEmpty ? "—" : value}</span>
    </div>
  );
};

const PoolInfoCard = ({
  pool,
  isEditing,
  setIsEditing,
  infoForm,
  setInfoForm,
  setPool,
}) => {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInfoForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setFormError("");
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === "name" && !value) {
      setErrors((prev) => ({ ...prev, name: "Pool Name is required." }));
    }
  };

  const handleSaveInfo = async (e) => {
    e.preventDefault();
    if (!infoForm.name) {
      setErrors((prev) => ({ ...prev, name: "Pool Name is required." }));
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const payload = {
        name: infoForm.name,
        description: infoForm.description,
      };
      const res = await api.put(`/client-pools/${pool.id}`, payload);

      if (res.data.success) {
        setPool(res.data.data);
        setIsEditing(false);
      }
    } catch (error) {
      console.error(error);
      setFormError(error.response?.data?.message || "Failed to update pool info");
    } finally {
      setSaving(false);
    }
  };

  if (isEditing) {
    return (
      <section>
        <div className="mb-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Edit Pool Info
          </h3>
        </div>
        <form
          onSubmit={handleSaveInfo}
          noValidate
          className="bg-white shadow-sm border border-gray-100 rounded-lg p-5 space-y-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 items-start">
            <FormInput
              label="Pool Name"
              name="name"
              value={infoForm.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.name}
              required
            />
            <div />
            <div className="md:col-span-2">
              <FormTextarea
                label="Description"
                name="description"
                value={infoForm.description}
                onChange={handleChange}
                rows={3}
              />
            </div>
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
              {formError}
            </div>
          )}

          <div className="pt-3 border-t border-gray-100 space-y-3">
            <span className="block text-xs text-gray-500">
              Fields marked with <span className="text-red-500">*</span> are mandatory.
            </span>
            <div className="flex gap-2 text-sm">
              <button
                type="submit"
                disabled={saving}
                className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                disabled={saving}
                className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </section>
    );
  }

  return (
    <section>
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Pool Info
        </h3>
        <button
          onClick={() => setIsEditing(true)}
          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
        >
          Update
        </button>
      </div>
      <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
        <div className="space-y-3">
          <Field label="Pool Name" value={pool.name} />
          <Field label="Number of Clients" value={pool.clients?.length ?? 0} />
          <Field label="Number of Managers" value={pool.managers?.length ?? 0} />
        </div>
        <div className="space-y-3">
          <Field label="Description" value={pool.description} />
        </div>
      </div>
    </section>
  );
};

export default PoolInfoCard;
